import type {
  PendingTenantAttachment,
  Thread,
  ThreadAssociation,
} from '../types/relay-types';

/**
 * Triage thread bucketing — codifies the classify-vs-review distinction that
 * drives the Otto rail landing. The confirm-gate predicate here is the single
 * source of truth shared by the mutation auto-confirm/auto-revert logic
 * (`useRelayMutations`) and the card's confirm affordance (`UpdatesThreadCard`).
 */

/**
 * The confirm gate: a thread is confirmable iff it has ≥1 association and —
 * when required types are configured — at least one association of a required
 * type is present. Keyed on `requiredTypeIds` (the tenant config) rather than
 * the per-association `isRequiredType` flag, because optimistically-added
 * associations don't carry that flag until the server responds. This is the
 * one predicate every confirm/auto-confirm/auto-revert call site must use so
 * they can't drift apart.
 *
 * Mirrors the backend gate (`ThreadBusinessRules.validate_can_confirm`):
 * reads only `objectTypeId`, so a type-only ("Category") association
 * satisfies the gate BY DESIGN — "recognizably about a Customer" is enough,
 * a specific record is not required. Keep in lockstep with the backend.
 */
export function satisfiesConfirmGate(
  associations: Array<Pick<ThreadAssociation, 'objectTypeId'>>,
  requiredTypeIds: string[]
): boolean {
  if (associations.length === 0) return false;
  if (requiredTypeIds.length === 0) return true;
  return associations.some((a) => requiredTypeIds.includes(a.objectTypeId));
}

/** A thread with zero associations — nothing proposed yet, needs classifying. */
export function isUnclassified(thread: Thread): boolean {
  return thread.associations.length === 0;
}

/**
 * A thread with proposed-but-unconfirmed associations awaiting human review:
 * not yet confirmed, but satisfies the confirm gate.
 */
export function isPendingConfirmation(
  thread: Thread,
  requiredTypeIds: string[]
): boolean {
  if (thread.associationConfirmed) return false;
  return satisfiesConfirmGate(thread.associations, requiredTypeIds);
}

export type TriageBuckets = {
  unclassified: Thread[];
  pendingConfirmation: Thread[];
};

/** Partition threads into the two triage buckets the landing chips act on. */
export function getTriageBuckets(
  threads: Thread[],
  requiredTypeIds: string[]
): TriageBuckets {
  const unclassified: Thread[] = [];
  const pendingConfirmation: Thread[] = [];
  for (const thread of threads) {
    if (isUnclassified(thread)) {
      unclassified.push(thread);
    } else if (isPendingConfirmation(thread, requiredTypeIds)) {
      pendingConfirmation.push(thread);
    }
  }

  return {unclassified, pendingConfirmation};
}

/**
 * Pending attachments whose parent thread has left the feed (e.g. the thread was
 * confirmed while the attachment stayed pending). These graduate to standalone
 * feed cards — and must be counted by the triage badge so it stays in lockstep
 * with the feed. Shared by `RelayContainer` (feed) and `useTriageCount` (badge).
 */
export function getOrphanedAttachments(
  threads: Thread[],
  pendingAttachments: PendingTenantAttachment[]
): PendingTenantAttachment[] {
  const threadIds = new Set(threads.map((t) => t.id));
  return pendingAttachments.filter((a) => !threadIds.has(a.threadId));
}
